/**
 * AudioScheduler - WAAClock-based audio scheduler
 *
 * Replaces Tone.js Transport with a lightweight scheduler based on the
 * "Tale of Two Clocks" pattern for precise Web Audio timing.
 *
 * @see https://web.dev/articles/audio-scheduling
 */

import WAAClock, { type WAAClockEvent } from 'waaclock';
import { setInterval as workerSetInterval, clearInterval as workerClearInterval } from 'worker-timers';

export interface SchedulerOptions {
  /** How far ahead to schedule events (seconds). Default: 0.1 */
  lookAhead?: number;
  /** How often to check for events to schedule (ms). Default: 25 */
  scheduleInterval?: number;
  /** Tolerance before deadline (seconds). Default: 0.001 */
  toleranceEarly?: number;
  /** Tolerance after deadline (seconds). Default: 0.1 */
  toleranceLate?: number;
}

export interface ScheduledEvent {
  /** The scheduled deadline (absolute audio time) */
  deadline: number;
  /** Interval for repeating events (null if one-shot) */
  repeatInterval: number | null;
  /** Internal WAAClock event reference */
  _waacEvent: WAAClockEvent | null;
}

export type SchedulerCallback = (event: ScheduledEvent) => void;

const DEFAULT_OPTIONS: Required<SchedulerOptions> = {
  lookAhead: 0.1,
  scheduleInterval: 25,
  toleranceEarly: 0.001,
  toleranceLate: 0.1,
};

const MIN_BPM = 20;
const MAX_BPM = 300;

const BACKGROUND_LOOKAHEAD_MULTIPLIER = 10; // 10x look-ahead when backgrounded

export class AudioScheduler {
  private context: AudioContext;
  private clock: WAAClock | null = null;
  private tickIntervalId: number | null = null;
  private events: Map<ScheduledEvent, WAAClockEvent> = new Map();

  private _bpm = 120;
  private _swing = 0;
  private _lookAhead: number;
  private _baseLookAhead: number;
  private _scheduleInterval: number;
  private _toleranceEarly: number;
  private _toleranceLate: number;
  private _isRunning = false;
  private _isDisposed = false;

  constructor(context: AudioContext, options: SchedulerOptions = {}) {
    this.context = context;

    const opts = { ...DEFAULT_OPTIONS, ...options };
    this._lookAhead = opts.lookAhead;
    this._baseLookAhead = opts.lookAhead;
    this._scheduleInterval = opts.scheduleInterval;
    this._toleranceEarly = opts.toleranceEarly;
    this._toleranceLate = opts.toleranceLate;
  }

  // ==================== Getters ====================

  get isRunning(): boolean {
    return this._isRunning;
  }

  get bpm(): number {
    return this._bpm;
  }

  get swing(): number {
    return this._swing;
  }

  get lookAhead(): number {
    return this._lookAhead;
  }

  get scheduleInterval(): number {
    return this._scheduleInterval;
  }

  get currentTime(): number {
    return this.context.currentTime;
  }

  get pendingEventCount(): number {
    return this.events.size;
  }

  // ==================== Setters ====================

  setBPM(bpm: number): void {
    this._bpm = Math.max(MIN_BPM, Math.min(MAX_BPM, bpm));
  }

  setSwing(swing: number): void {
    this._swing = Math.max(0, Math.min(1, swing));
  }

  // ==================== Playback Control ====================

  start(): void {
    if (this._isDisposed || this._isRunning) return;

    // Create WAAClock with manual tick method (we control timing via worker-timers)
    this.clock = new WAAClock(this.context, {
      tickMethod: 'manual',
      toleranceEarly: this._toleranceEarly,
      toleranceLate: this._toleranceLate,
    });

    this.clock.start();

    // Use worker-timers for background-tab-resistant scheduling
    this.tickIntervalId = workerSetInterval(() => {
      this.tick();
    }, this._scheduleInterval);

    this._isRunning = true;
  }

  stop(): void {
    if (!this._isRunning) return;

    this.cancelAll();

    if (this.tickIntervalId !== null) {
      workerClearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }

    if (this.clock) {
      // WAAClock.stop() fails when tickMethod is 'manual' because no _clockNode exists
      // We just need to mark it as stopped and clear events
      try {
        this.clock.stop();
      } catch {
        // Expected when using 'manual' tickMethod
        this.clock._started = false;
      }
      this.clock = null;
    }

    this._isRunning = false;
  }

  pause(): void {
    if (!this._isRunning) return;

    if (this.tickIntervalId !== null) {
      workerClearInterval(this.tickIntervalId);
      this.tickIntervalId = null;
    }

    this._isRunning = false;
  }

  resume(): void {
    if (this._isDisposed || this._isRunning || !this.clock) return;

    this.tickIntervalId = workerSetInterval(() => {
      this.tick();
    }, this._scheduleInterval);

    this._isRunning = true;
  }

  dispose(): void {
    this.stop();
    this._isDisposed = true;
  }

  // ==================== Scheduling ====================

  /**
   * Schedule a callback at an absolute audio time
   */
  scheduleAt(callback: SchedulerCallback, deadline: number): ScheduledEvent | null {
    if (!this._isRunning || !this.clock || this._isDisposed) {
      return null;
    }

    const event: ScheduledEvent = {
      deadline,
      repeatInterval: null,
      _waacEvent: null,
    };

    const waacEvent = this.clock.callbackAtTime((waacEvt) => {
      callback(event);
      // Clean up one-shot events
      if (!event.repeatInterval) {
        this.events.delete(event);
      }
    }, deadline);

    event._waacEvent = waacEvent;
    this.events.set(event, waacEvent);

    return event;
  }

  /**
   * Schedule a callback after a delay (relative to current time)
   */
  scheduleAfter(callback: SchedulerCallback, delay: number): ScheduledEvent | null {
    return this.scheduleAt(callback, this.context.currentTime + delay);
  }

  /**
   * Schedule a repeating callback
   */
  scheduleRepeating(
    callback: SchedulerCallback,
    startTime: number,
    interval: number
  ): ScheduledEvent | null {
    if (!this._isRunning || !this.clock || this._isDisposed) {
      return null;
    }

    const deadline = startTime <= 0 ? this.context.currentTime : startTime;

    const event: ScheduledEvent = {
      deadline,
      repeatInterval: interval,
      _waacEvent: null,
    };

    const waacEvent = this.clock
      .callbackAtTime((waacEvt) => {
        callback(event);
        // Update deadline for next iteration
        event.deadline = waacEvt.deadline + interval;
      }, deadline)
      .repeat(interval);

    event._waacEvent = waacEvent;
    this.events.set(event, waacEvent);

    return event;
  }

  /**
   * Cancel a scheduled event
   */
  cancel(event: ScheduledEvent | null): void {
    if (!event || !event._waacEvent) return;

    event._waacEvent.clear();
    this.events.delete(event);
  }

  /**
   * Cancel all scheduled events
   */
  cancelAll(): void {
    const waacEvents = this.events.values();
    for (const waacEvent of waacEvents) {
      waacEvent.clear();
    }
    this.events.clear();
  }

  // ==================== Timing Utilities ====================

  /**
   * Manually tick the scheduler (called by worker-timers interval)
   */
  tick(): void {
    if (this.clock && this._isRunning) {
      this.clock.tick();
    }
  }

  /**
   * Calculate swing offset for a beat
   * @param beatIndex - The beat number (0-indexed)
   * @param noteDuration - Duration of the note in seconds
   * @returns Offset in seconds to add to the scheduled time
   */
  calculateSwingOffset(beatIndex: number, noteDuration: number): number {
    if (this._swing === 0) return 0;

    // Only apply swing to odd beats (1, 3, 5, etc.)
    if (beatIndex % 2 === 0) return 0;

    // Swing shifts the off-beat notes later
    // Maximum swing is 1/3 of the note duration (triplet feel)
    const maxSwingOffset = noteDuration * 0.33;
    return maxSwingOffset * this._swing;
  }

  /**
   * Convert beats to seconds based on current BPM
   */
  beatsToSeconds(beats: number): number {
    return (beats * 60) / this._bpm;
  }

  /**
   * Convert seconds to beats based on current BPM
   */
  secondsToBeats(seconds: number): number {
    return (seconds * this._bpm) / 60;
  }

  /**
   * Get the offset between audio clock and performance clock
   * Used for MIDI timestamp synchronization
   */
  getMidiTimestampOffset(): number {
    return this.context.currentTime * 1000 - performance.now();
  }

  /**
   * Convert audio time to MIDI timestamp (DOMHighResTimeStamp)
   */
  audioTimeToMidiTimestamp(audioTime: number): number {
    const offset = this.getMidiTimestampOffset();
    return audioTime * 1000 + offset;
  }

  // ==================== Background Tab Handling ====================

  /**
   * Handle visibility change (tab focus/blur)
   * Increases look-ahead when tab is backgrounded for more reliable scheduling
   */
  handleVisibilityChange(isVisible: boolean): void {
    if (isVisible) {
      this._lookAhead = this._baseLookAhead;
    } else {
      // Increase look-ahead significantly when backgrounded
      this._lookAhead = this._baseLookAhead * BACKGROUND_LOOKAHEAD_MULTIPLIER;
    }
  }
}

export default AudioScheduler;
